import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Home.css';
import { Logo1 } from '../../Assests/Assests';
import { responsiveProps } from '../../utils/imageSrc';
import HeroRepulsion from './HeroRepulsion';

const ADMIN_API = 'https://admin.riymay.com/api/v1/admin';

/* Both numbers below come straight out of Home.css.

   SHELF CARD (.home-product-img, width: 100% of its grid track)
     .home-latest pads 60px per side and .home-shelf-grid is 4 equal columns
     with a 16px gap:
         (100vw - 120 - 48) / 4  = calc(25vw - 42px)
     Under 768px the padding drops to 20px, the grid to 2 columns, the gap
     to 10px:
         (100vw - 40 - 10) / 2   = calc(50vw - 25px)

   COLLECTION BANNER (.home-collection-bg, absolutely filling a full-width
   21:9 .home-collection-banner) is the section's content box:
         >768px    calc(100vw - 120px)
         <=768px   calc(100vw - 40px)
   It grows to scale(1.02) on hover, which is inside the rounding here.

   The hero background is a CSS `background-image`, not an <img>, so srcset
   cannot reach it — it is left exactly as it was. */
const HOME_SHELF_SIZES =
  '(max-width: 768px) calc(50vw - 25px), calc(25vw - 42px)';
const HOME_BANNER_SIZES =
  '(max-width: 768px) calc(100vw - 40px), calc(100vw - 120px)';

const Home = () => {
  const [shelves, setShelves] = useState([]);
  const [banners, setBanners] = useState([]);
  const [heroMedia, setHeroMedia] = useState(null);
  // `settled` = the hero media request finished, either way. `armed` = this
  // component mounted, so JS is definitely running and it is safe to hold the
  // headline back. `ready` = the hero photo has actually arrived (or we gave
  // up waiting), which is the moment the entrance animation should play.
  const [heroSettled, setHeroSettled] = useState(false);
  const [heroArmed, setHeroArmed] = useState(false);
  const [heroReady, setHeroReady] = useState(false);
  const navigate = useNavigate();
  const heroTitleWrapRef = useRef(null);

  useEffect(() => {
    fetch(`${ADMIN_API}/homepage/data`)
      .then(r => r.json())
      .then(d => {
        setShelves(d.shelves || []);
        setBanners(d.banners || []);
      })
      .catch(() => {});

    fetch(`${ADMIN_API}/hero-section/media`)
      .then(r => r.json())
      .then(d => {
        const active = (d.heroSectionMedia || []).find(m => m.activeStatus === 'active');
        setHeroMedia(active || null);
        setHeroSettled(true);
      })
      .catch(() => setHeroSettled(true));

    setHeroArmed(true);
  }, []);

  // Hold the hero entrance until the background photo is on screen.
  //
  // The animation is 820ms and starts the instant the CSS parses. The photo
  // cannot even begin downloading until the media request above answers, and
  // it is a multi-megabyte JPEG, so it routinely lands well after the motion
  // has finished. The headline was animating into an empty frame and sitting
  // still by the time there was anything to look at -- the animation ran
  // perfectly and nobody ever saw it.
  //
  // Every path ends with the hero shown: image loaded, image failed, no media
  // configured, or a slow connection that trips the cutoff. The hidden start
  // state is applied only once this component has mounted, so if the bundle
  // never runs the headline is simply visible and static.
  useEffect(() => {
    if (!heroSettled) return undefined;

    let cancelled = false;
    const show = () => { if (!cancelled) setHeroReady(true); };

    if (!heroMedia) { show(); return undefined; }

    const img = new Image();
    img.onload = show;
    img.onerror = show;
    img.src = `https://riymay.com/assets/${heroMedia.fileName}`;
    if (img.complete) show();

    // A stalled download must never leave the headline hidden.
    const cutoff = setTimeout(show, 2500);

    return () => { cancelled = true; clearTimeout(cutoff); };
  }, [heroSettled, heroMedia]);

  const heroStyle = heroMedia
    ? { backgroundImage: `url(https://riymay.com/assets/${heroMedia.fileName})` }
    : {};

  return (
    <div className="home-page">

      {/* ── HERO ── */}
      <section
        className={`home-hero${heroArmed ? ' is-armed' : ''}${heroReady ? ' is-ready' : ''}`}
        style={heroStyle}
        id="home-hero"
      >
        <div className="home-hero-overlay" />

        <div className="home-hero-stage">
          {/* The wrap gives HeroRepulsion a sizing box that exactly matches
              the real headline; the headline's own markup, text and entrance
              animation are completely unchanged. See HeroRepulsion.jsx. */}
          <div className="hero-repel-wrap" ref={heroTitleWrapRef}>
            <h1 className="home-hero-title">
              <span className="home-hero-line"><span>Wear the mask.</span></span>
              <span className="home-hero-line"><span>Own the silence.</span></span>
            </h1>
            <HeroRepulsion active={heroReady} wrapRef={heroTitleWrapRef} />
          </div>
          <p className="home-hero-lede">
            Premium materials, unisex silhouettes, precision finishing. Built to outlast seasons.
          </p>
          <Link to="/newproduct" className="home-hero-cta">Explore the collection</Link>
        </div>

        <div className="home-hero-scroll">
          <img src={Logo1} alt="" className="home-hero-scroll-logo" draggable={false} />
          <span>Scroll</span>
        </div>
      </section>

      {/* ── LATEST DROP ── */}
      {shelves.length > 0 && (
        <section className="home-latest">
          <div className="home-section-label">Latest drop</div>
          {shelves.map(shelf => (
            <div key={shelf._id} className="home-shelf">
              <h2 className="home-shelf-name">{shelf.name}</h2>
              <div className="home-shelf-grid">
                {shelf.products.map(product => (
                  <div
                    key={product._id}
                    className="home-product-card"
                    onClick={() => navigate(`/product/${product._id}`)}
                  >
                    {product.images && product.images[0] ? (
                      <img
                        src={`https://riymay.com/assets/${product.images[0].url}`}
                        alt={product.name}
                        className="home-product-img"
                        {...responsiveProps(product.images[0], HOME_SHELF_SIZES)}
                      />
                    ) : (
                      <div className="home-product-img-placeholder" />
                    )}
                    <div className="home-product-info">
                      <span className="home-product-name">{product.name}</span>
                      <span className="home-product-price">₹{product.price}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* ── COLLECTIONS ── */}
      {banners.length > 0 && (
        <section className="home-collections">
          <div className="home-section-label">Collections</div>
          {banners.map(banner => (
            <Link key={banner._id} to={banner.link || '/products-collection'} className="home-collection-banner">
              {banner.imageUrl && (
                <img
                  src={`https://riymay.com/assets/${banner.imageUrl}`}
                  alt={banner.title}
                  className="home-collection-bg"
                  /* A banner is not a product image record: it carries a bare
                     `imageUrl` string rather than { url, widths }. Reshaped
                     here so the same helper can read it the moment the banner
                     payload starts carrying a `widths` array of its own.
                     Until then this is null and the <img> is untouched. */
                  {...responsiveProps(
                    { url: banner.imageUrl, widths: banner.widths },
                    HOME_BANNER_SIZES
                  )}
                />
              )}
              <div className="home-collection-overlay" />
              <div className="home-collection-content">
                <span className="home-collection-title">{banner.title}</span>
                <span className="home-collection-cta">Explore →</span>
              </div>
            </Link>
          ))}
        </section>
      )}

    </div>
  );
};

export default Home;
